import { ActionIcon, Button, Group, Menu, Modal, Text } from '@mantine/core';
import { IconArchive, IconCopy, IconDots, IconPencil, IconStar } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { showSuccess, showSuccessWithAction } from 'src/toasts/toasts';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { RenameModelModal } from '../RenameModelModal';
import { ScenarioScope } from '../scope';
import { ScenarioSummary } from '../types';
import { ModelGroup, peopleIn } from './groups';
import classes from './ModelList.module.css';
import { DuplicateModelModal } from './DuplicateModelModal';
import { joinNames } from '../../names';
import { modelTarget } from './modelParts';
import { useModelActions } from './useModelActions';

type Props = {
  scenario: ScenarioSummary;
  scope: ScenarioScope;
  group: ModelGroup;
  names: Record<string, string>;
  // Takes the adviser to the Archived tab, which the archive toast offers.
  onViewArchived: () => void;
};

/** Whose primary model a model is or would be: "the joint", or "Tom's". */
const primaryOwner = (group: ModelGroup, people: string[]): string => {
  if (group.joint) return 'the joint';
  return people[0] ? `${people[0]}'s` : 'the';
};

/** Rename, duplicate, make primary and archive, on any active model in the list. */
export function ModelMenu({ scenario, scope, group, names, onViewArchived }: Props) {
  const { updateModel, duplicateModel } = useModelActions();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<'rename' | 'duplicate' | 'archivePrimary' | null>(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const isPrimary = group.primary === scenario;
  const people = peopleIn(group, names);
  const owner = primaryOwner(group, people);

  const rename = async (label: string) => {
    try {
      await updateModel({ scenario, update: { label } });
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be renamed.', error);
    }
  };

  // The copy opens straight away, ready to change.
  const duplicate = async (label: string) => {
    setIsDuplicating(true);
    try {
      const copy = await duplicateModel({ scenario, label });
      setDialog(null);
      navigate(modelTarget(copy, scope));
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be duplicated.', error);
    } finally {
      setIsDuplicating(false);
    }
  };

  const makePrimary = async () => {
    try {
      await updateModel({ scenario, update: { isPrimary: true } });
      showSuccess(`${scenario.label} is now ${owner} primary model`);
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be made primary.', error);
    }
  };

  const archive = async () => {
    setDialog(null);
    try {
      await updateModel({ scenario, update: { archived: true } });
      showSuccessWithAction(`${scenario.label} archived`, {
        label: 'View archived',
        onClick: onViewArchived,
      });
    } catch (error) {
      notifyAndLogError('Cashflow model', 'The model could not be archived.', error);
    }
  };

  return (
    <>
      <Menu position="bottom-end" shadow="md" width={200}>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            className={classes.aboveLink}
            aria-label={`Actions for ${scenario.label}`}
          >
            <IconDots size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => setDialog('rename')}>
            Rename
          </Menu.Item>
          <Menu.Item leftSection={<IconCopy size={16} />} onClick={() => setDialog('duplicate')}>
            Duplicate
          </Menu.Item>
          {!isPrimary && (
            <Menu.Item leftSection={<IconStar size={16} />} onClick={() => void makePrimary()}>
              Make primary
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconArchive size={16} />}
            // Archiving a primary model leaves its people without one, so that asks first.
            onClick={() => (isPrimary ? setDialog('archivePrimary') : void archive())}
          >
            Archive
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <RenameModelModal
        key={`rename-${dialog === 'rename'}`}
        opened={dialog === 'rename'}
        label={scenario.label}
        onClose={() => setDialog(null)}
        onRename={label => void rename(label)}
      />
      <DuplicateModelModal
        key={`duplicate-${dialog === 'duplicate'}`}
        opened={dialog === 'duplicate'}
        label={scenario.label}
        isDuplicating={isDuplicating}
        onClose={() => setDialog(null)}
        onDuplicate={label => void duplicate(label)}
      />
      <Modal
        opened={dialog === 'archivePrimary'}
        onClose={() => setDialog(null)}
        title={`Archive "${scenario.label}"?`}
        centered
      >
        <Text size="sm">
          This is {owner} primary model. Archiving it leaves{' '}
          {people.length > 0 ? joinNames(people) : 'them'} with no primary model until you make
          another of their models primary. You can restore it from the Archived tab.
        </Text>
        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" size="xs" onClick={() => setDialog(null)}>
            Cancel
          </Button>
          <Button size="xs" onClick={() => void archive()}>
            Archive model
          </Button>
        </Group>
      </Modal>
    </>
  );
}
